import { api } from '@/config/api';

export type AdRuleMetric = 'spend' | 'cpl' | 'cpc' | 'ctr' | 'impressions' | 'clicks' | 'leads';
export type AdRulePlatform = 'meta' | 'tiktok';

export interface AdRuleTrigger {
  at: string;
  entityId: string;
  entityName?: string;
  metricValue: number;
  action: 'pause' | 'notify';
  ok: boolean;
  error?: string;
}

export interface AdRule {
  ruleUid: string;
  companyUid: string;
  name: string;
  platform: AdRulePlatform;
  scopeCampaignIds: string[];
  metric: AdRuleMetric;
  operator: 'gt' | 'lt';
  threshold: number;
  lookbackDays: number;
  minSpend: number;
  action: 'pause' | 'notify';
  isActive: boolean;
  lastRunAt?: string;
  lastTriggeredAt?: string;
  recentTriggers: AdRuleTrigger[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateAdRulePayload {
  name: string;
  platform: AdRulePlatform;
  scopeCampaignIds?: string[];
  metric: AdRuleMetric;
  operator: 'gt' | 'lt';
  threshold: number;
  lookbackDays?: number;
  minSpend?: number;
  action: 'pause' | 'notify';
  isActive?: boolean;
}

export interface CampaignMetricsRow {
  entityId: string;
  spend: number;
  impressions: number;
  clicks: number;
  leads: number;
  cpl: number | null;
  cpc: number | null;
  ctr: number | null;
  /** The ad account's billing currency (what spend/cpc/cpl are in). */
  currency?: string | null;
  /** The company's currency, and the money metrics converted into it at today's rate. */
  companyCurrency?: string | null;
  spendConverted?: number | null;
  cplConverted?: number | null;
  cpcConverted?: number | null;
  ratesAsOf?: string | null;
}

const BASE = '/slighter_crm/ad-rules';

export const adRulesService = {
  list: async (): Promise<AdRule[]> => (await api.get(BASE)).data,
  get: async (uid: string): Promise<AdRule> => (await api.get(`${BASE}/${uid}`)).data,
  create: async (p: CreateAdRulePayload): Promise<AdRule> => (await api.post(BASE, p)).data,
  update: async (uid: string, p: Partial<CreateAdRulePayload>): Promise<AdRule> =>
    (await api.patch(`${BASE}/${uid}`, p)).data,
  remove: async (uid: string): Promise<{ ok: boolean }> => (await api.delete(`${BASE}/${uid}`)).data,
  /** A/B compare — cached metrics for 2-4 campaigns of one platform. */
  compare: async (platform: AdRulePlatform, campaignIds: string[], lookbackDays = 7): Promise<CampaignMetricsRow[]> =>
    (await api.get(`${BASE}/compare`, {
      params: { platform, campaignIds: campaignIds.join(','), lookbackDays },
    })).data,
};
